/**
 * @file        src/pages/flight/TicketDoc.jsx
 * @description Admin-side printable flight E-TICKET / itinerary. Rendered inside
 *              the console (no redirect to the customer site) from the admin
 *              API's own booking details. This is the SAME document as the
 *              customer's copy (each storefront's src/pages/Voucher/FlightVoucher):
 *              branded header + status, the E-Ticket No. / Booking Ref / PNR /
 *              Trip Type meta strip, per-flight airline PNRs, the scannable BCBP
 *              boarding barcode, the consolidated itinerary table, per-sector
 *              passenger rows, GST details, the itemised Fare Breakdown, booking
 *              notes, the travel-insurance policy, travel instructions and the
 *              carrier disclaimer.
 * @author      Kinetika Labs
 * @created     2026-07-10
 * @changelog
 *   - 2026-10-08 Claude — Convenience-fee / Discount lines print the admin-given rule names (fare.convenience_label / fare.discount_label); generic label as fallback.
 *   - 2026-10-06 Claude — Fare block: Base Fare includes the processing fee; the Convenience Fee row takes its place and always shows.
 *                          Client decision: the Processing fee (our markup) is not
 *                          a line of its own — it is inside the Base fare, and the
 *                          Convenience fee (shown even at ₹0) takes its place.
 *                          Display only: no total or stored figure changes.
 *   - 2026-09-25 Claude — The Fare Breakdown reads `b.fare.summary` — composed
 *                              server-side from this booking's own ledger columns by
 *                              the mirror of the module the CUSTOMER's copy is built
 *                              from — instead of deriving the Processing Fee from
 *                              `fd.markup` and printing the leftover as "Other
 *                              adjustments". Live: this console showed "Other
 *                              adjustments −₹2.00" while the storefront showed
 *                              "Discount −₹2.00" for the same rupee, because both
 *                              preferred a `checkout` blob that said 0 over the
 *                              ₹2.00 recorded in `discount_amount`. See §30.
 *   - 2026-09-23 Claude — The per-sector preference cell no longer prints one
 *                          add-on twice. `confirmed_addons` carries no passenger,
 *                          so two travellers who chose the SAME meal on the same
 *                          sector produced two indistinguishable rows and the old
 *                          "does any selection with this code carry my paxIndex"
 *                          test passed for both, on both rows ("Baby Meal, Baby
 *                          Meal"). Attribution moved to lib/paxSectorDetail's
 *                          consuming pair-up, shared with BookingDetailsDrawer —
 *                          which had the same defect and, unlike this document,
 *                          was not even consulting the supplier's own ssrByRoute.
 *                          ticketNoOf moved to the same module unchanged.
 *   - 2026-09-23 Claude — "Back to Bookings" returns to the list this document
 *                          was opened from. This route is shared by the B2C and
 *                          the Corporate screen, so Back always landed on the
 *                          B2C list; lib/docOrigin carries which it was.
 *   - 2026-09-11 Claude — Prints every per-flight frequent-flyer membership via
 *                          lib/frequentFlyer (the customer copy does the same).
 *   - 2026-09-10 Claude — The admin copy of this ticket disagreed with the
 *                          customer's on PNR, seat/meal and baggage, because the
 *                          two were fed from different sources: the customer route
 *                          enriches the stored booking with a live TripJack
 *                          booking-details read, the admin route returned the
 *                          stored JSON raw. Admin's /bookings/:id/details now
 *                          merges that same enriched payload, so segments carry
 *                          their OWN airline PNR (a connecting ticket printed the
 *                          booking-level PNR on every sector), `ssrByRoute` is
 *                          populated so prefOf() stops falling back to
 *                          confirmed_addons, and baggage is the allowance the
 *                          TICKET carries rather than the book-time review copy.
 *                          The BCBP seat now prefers the supplier's live seat
 *                          (`t.seat`) over the confirmed_addons reconstruction, so
 *                          both PDF417s encode the same boarding data.
 *   - 2026-09-09 Claude — Matched to the customer voucher (FlightVoucher.jsx), the
 *                          reference document. (a) Airline logos come from the same
 *                          bundled /airlines/<IATA>.png set via the ported
 *                          AirlineLogo, not the pics.avs.io CDN inside a white
 *                          bordered box — the AI Express mark printed blurred and
 *                          framed. (b) The route's city / airport caption lines no
 *                          longer use className="sub": that collides with the SIDEBAR
 *                          sub-menu rule in admin.css, which drew a ::before dot,
 *                          pointer cursor and hover pill, so they looked like
 *                          clickable radio options. (c) Terminal no longer prefixes
 *                          "Terminal " onto a value that already says it ("Terminal
 *                          Terminal 1"). (d) Depart/Arrive prefer the supplier's own
 *                          dep/arr strings before deriving from the ISO timestamp.
 *   - 2026-09-09 Claude — Header always renders a LOGO, via the shared
 *                          components/DocBrandMark (storefront logo → tenant
 *                          admin-portal logo → the green BrandWordmark SVG the
 *                          customer site shows). Neither logo column is
 *                          populated on most tenants, so in practice every
 *                          e-ticket printed the plain-text COMPANY.legal
 *                          fallback instead of a logo.
 *   - 2026-09-08 Claude — The boarding barcode encoded a DIFFERENT BCBP string
 *                          from the customer's copy of the same ticket, so the
 *                          two PDF417s did not match (and the admin reprint told
 *                          the gate the passenger held no seat). Every other
 *                          field of the payload — PNR, sector, carrier, flight
 *                          number, departure, cabin, name — comes from the same
 *                          stored booking JSON on both sides; the SEAT did not.
 *                          The customer voucher reads `passengers[].seat` from
 *                          the supplier's live ssrSeatInfos, while this page read
 *                          `t.seat`, which is ALWAYS null here: the checkout
 *                          traveller payload carries no seat (seats travel in
 *                          their own `seats[]` → selectedAddons), and the
 *                          `flight_passengers` seat_number the details endpoint
 *                          merges is never written — booking_backend's
 *                          flightPassengersModel has no call sites. So the seat
 *                          field always encoded '0000'. It is now derived from
 *                          the add-ons the supplier CONFIRMED (seatsOfPax),
 *                          matching both the customer's barcode and the same
 *                          confirmed-not-selected rule the Meal & Seat column
 *                          already follows.
 *   - 2026-09-07 Claude — PARITY with the customer e-ticket, which had moved on
 *                          while this copy had not: the two PDFs of ONE booking
 *                          printed different documents. Ported from
 *                          FlightVoucher.jsx —
 *                          (a) THE FARE SUMMARY WAS WRONG, not merely different.
 *                              It read `fare.convenience_fee` under the label
 *                              "Management Fee", derived the management-fee GST
 *                              as 18% of that, and read `fare.addon_charges` /
 *                              `fare.baggage_protection_fee` — two fields the
 *                              details API has never returned, so both printed
 *                              ₹0.00 forever. Nothing billed the Processing Fee
 *                              or the Convenience Fee at all, so the printed
 *                              lines did not add up to the Total Price they sat
 *                              above (₹999 + ₹1,932.50 − ₹1,000 against a
 *                              ₹2,231.50 total on TJS101202938500 — short by the
 *                              ₹300 margin). Every line now comes from the stored
 *                              fareDetail, with an "Other adjustments" residual
 *                              so the lines can never silently disagree with the
 *                              total again;
 *                          (b) branded header (the booking's own STOREFRONT logo
 *                              via brands.logo_url), status pill, and the
 *                              E-Ticket No. / Booking Ref / PNR / Trip Type /
 *                              Status meta strip;
 *                          (c) one airline-PNR row PER FLIGHT (a connecting or
 *                              return trip is ticketed per sector, each with its
 *                              own PNR — heading the page with only the first hid
 *                              the rest);
 *                          (d) the boarding barcode is now the same scannable
 *                              PDF417 BCBP the customer's copy carries, one per
 *                              journey group. The admin printed a Code 39 of the
 *                              bare PNR, which no airline gate can read;
 *                          (e) Passenger Details is one row PER SECTOR (rowSpan)
 *                              with the traveller's DOB / passport / FF block,
 *                              that sector's own PNR + ticket number, its own
 *                              baggage allowance and its own meal/seat — gated on
 *                              what the supplier CONFIRMED, never the raw
 *                              selection;
 *                          (f) added GST Details, Booking Notes and the Travel
 *                              Insurance policy block (incl. the "premium charged
 *                              but no policy issued" warning);
 *                          (g) Important Travel Instructions + the Terms &
 *                              Conditions / Disclaimer replace the old numbered
 *                              "Important Information" list;
 *                          (h) the page-2 Dangerous Goods sheet is dropped — the
 *                              customer's e-ticket has no such page, and this
 *                              document has to be the same one.
 *   - 2026-08-05 Claude — CR C1/C2 minimalist redesign: the barcode now lives in
 *                          its own dedicated, named "Boarding Verification" zone
 *                          right under the PNR header (was appended after the
 *                          passenger table, easy to miss). The itinerary is now a
 *                          consolidated table across every leg/segment.
 *   - 2026-08-04 Claude — CR D2: accepts an optional `bookingIdOverride` prop
 *                          that short-circuits useParams() — lets ShareEmailModal
 *                          mount this component off-screen (bookingIdOverride=
 *                          booking.id) for silent PDF capture without navigating.
 *   - 2026-08-04 Claude — Sector middle is a directional runway matching the
 *                          customer voucher's Sector row.
 *   - 2026-07-10 Claude — Initial (admin-native e-ticket; replaces the deep link).
 *   - 2026-08-10 Claude — Hard status gate via docAccess(): an e-ticket only
 *                          exists for a CONFIRMED booking — pending /
 *                          cancellation-pending / cancelled bookings get a
 *                          clear "not available" notice instead of a printable
 *                          ticket.
 */
import { useEffect, Fragment } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../lib/docOrigin';
import { useQuery } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import BoardingBarcode, { bcbpString } from '../../components/BoardingBarcode';
import { getBookingDetails, listAmendments } from '../../api/flightBookings';
import AirlineLogo from '../../components/AirlineLogo';
import DocBrandMark from '../../components/DocBrandMark';
import { docAccess } from './cells';
import { useCompany } from '../../lib/useCompany';
import { ffLabels } from '../../lib/frequentFlyer';
import { ticketNoOf, confirmedAddonLabels, supplierSsrLabels } from '../../lib/paxSectorDetail';

const num = (v) => Number(v) || 0;
const round2 = (v) => Math.round(num(v) * 100) / 100;
const inr2 = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Per-STOREFRONT document tokens. The E-Ticket-No. prefix and the header accent
 * live in each storefront's own build (`NH-AIR-` + neon green in
 * booking_frontends/neon, `BD-AIR-` + teal in .../beyond), so the admin — one
 * console serving every brand — has to map them by the booking's brand code to
 * reprint the SAME number and look the customer already holds.
 *
 * A brand missing from this map falls back to its own code, which is honest
 * (`NEO-AIR-468` is at least traceable) but is NOT what that storefront prints:
 * add a new brand here when one is onboarded. Never default to another brand's
 * prefix — the ticket number is a support-desk lookup key.
 */
const BRAND_DOC = {
  neo: { prefix: 'NH', accent: '#0F9D6B' },
  bey: { prefix: 'BD', accent: '#12798C' },
};
const docTokensOf = (brand) => BRAND_DOC[String(brand || '').toLowerCase()]
  || { prefix: String(brand || 'AIR').toUpperCase().slice(0, 3), accent: '#0F9D6B' };

const fmtDateTime = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};
/** 2-letter IATA carrier code out of "IX", "IX1464", "6E-123" etc. */
const iataOf = (code) => String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '').match(/^[A-Z0-9]{2}/)?.[0] || '';
const fmtDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const d = new Date(`${m[0]}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};
const timeOf = (iso) => { const m = String(iso || '').match(/T(\d{2}):(\d{2})/); return m ? `${m[1]}:${m[2]}` : ''; };
const fmtMin = (min) => { const n = Math.max(0, Math.round(Number(min) || 0)); return `${Math.floor(n / 60)}h${n % 60 ? ` ${n % 60}m` : ''}`; };
const layoverMin = (a, b) => Math.round((new Date(b) - new Date(a)) / 60000);
const paxTypeName = (t) => ({ ADULT: 'Adult', CHILD: 'Child', INFANT: 'Infant' }[String(t || 'ADULT').toUpperCase()] || 'Adult');
/** "YYYY-MM-DD" → "DD/MM/YYYY" for the printed document (blank if unparseable). */
const voucherDmy = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || '');
};

// Airline logo. A thin wrapper over the SAME component and the SAME bundled
// /airlines/<IATA>.png set the customer's voucher uses, so both documents print
// an identical mark at identical sizes. It replaced a remote CDN (pics.avs.io)
// image drawn inside a white bordered, padded box — a different logo set, a
// frame the customer copy does not have, and a blank mark on any offline or
// network-blocked print.
function AirlineMark({ code, name, size = 30 }) {
  return <AirlineLogo code={iataOf(code)} label={name} size={size} />;
}

/** One label/value pair in the meta strip and the insurance/contact blocks. */
function Field({ label, value, mono }) {
  if (value == null || value === '') return null;
  return (
    <div className="tkt-field">
      <p className="tkt-field-label">{label}</p>
      <p className={`tkt-field-value${mono ? ' mono' : ''}`}>{value}</p>
    </div>
  );
}

/** One itemised charge line in the Fare Breakdown. `sub` renders the tax-code
 * breakdown / GST split under the label, exactly like the customer's copy. */
function FareRow({ label, amount, sub }) {
  return (
    <div className="tkt-fare-row">
      <span>
        {label}
        {sub ? <span className="tkt-fare-sub">{sub}</span> : null}
      </span>
      <b>{inr2(amount)}</b>
    </div>
  );
}

export default function TicketDoc({ bookingIdOverride } = {}) {
  const { id: routeId } = useParams();
  const id = bookingIdOverride ?? routeId;
  const navigate = useNavigate();
  // Which list to return to — this document route is shared by the B2C and the
  // Corporate screen (lib/docOrigin).
  const { search } = useLocation();
  const COMPANY = useCompany();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['flight-booking-details', Number(id)],
    queryFn: () => getBookingDetails(id),
  });
  const b = data?.data;
  // The amendment trail, for the reschedule charge + the "Reissued" chip. It is
  // its own endpoint on the admin side (the customer API folds it into the
  // booking record), so it is a second query rather than a wider payload. A
  // failure here must not block the ticket: an empty list just means no
  // reschedule line, which is the correct output for the vast majority.
  const { data: amendData } = useQuery({
    queryKey: ['flight-booking-amendments', Number(id)],
    queryFn: () => listAmendments(id),
    enabled: Boolean(id),
    retry: false,
  });
  const amendments = amendData?.data || [];

  useEffect(() => { if (b) document.title = `Flight E-Ticket ${b.booking_reference} · ${COMPANY.name}`; }, [b]);

  if (isLoading) return <div className="panel glass"><div className="empty">Preparing e-ticket…</div></div>;
  if (isError || !b) return <div className="panel glass"><div className="empty">Could not load this booking.</div></div>;

  // An e-ticket only exists for a CONFIRMED booking (client-approved rule) —
  // gated here too, not just at the buttons, so a pasted URL can't print one.
  // This is also why the customer's "not a valid boarding document" banner has no
  // counterpart below: an unconfirmed booking never reaches the document at all.
  if (!docAccess(b.booking_status).voucher) {
    return (
      <div className="panel glass">
        <div className="empty">
          An e-ticket is only issued for a confirmed booking — this booking is “{b.booking_status || 'not confirmed'}”.
          <div style={{ marginTop: 12 }}>
            <button className="btn ghost" onClick={() => navigate(backToList(search, '/flight/bookings', '/flight/corporate-bookings'))}>
              <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ref = b.booking_reference || '';
  const pnr = b.pnr_code || '—';
  // The status the DOCUMENT states, mapped exactly as the customer's copy maps
  // it: our internal "Booked" is a CONFIRMED ticket to the passenger, and the two
  // copies must not disagree about that. Pending is tested BEFORE confirmed —
  // "Awaiting confirmation" contains "confirm", and a substring must never
  // upgrade a not-ticketed booking to a confirmed e-ticket.
  const sRaw = String(b.booking_status || '').toLowerCase();
  const statusLabel = /fail/.test(sRaw) ? 'Failed'
    : /cancel/.test(sRaw) ? 'Cancelled'
      : /pend|await|initiat|process/.test(sRaw) ? 'Pending'
        : /success|book|confirm|ticket/.test(sRaw) ? 'Confirmed'
          : 'Pending';
  const doc = docTokensOf(b.brand);

  // E-Ticket No. is OUR flight_bookings.id, so a printed ticket points straight
  // at the row the console lists as "Flight Booking (ID-197)".
  const voucherNo = b.id
    ? `${doc.prefix}-AIR-${b.id}`
    : `${doc.prefix}-AIR-${String(ref).replace(/[^A-Za-z0-9]/g, '').slice(-8).toUpperCase()}`;

  const itinerary = b.legs || [];
  const paxAll = (b.passengers || []).map((p, i) => ({ ...p, _no: i + 1 }));
  const pax = paxAll;
  const fd = b.fare?.detail || null;
  const total = num(b.fare?.total_paid);

  // Trip type as PERSISTED at checkout — never re-derived from the leg airports
  // (a round trip returning to a different airport of the same city doesn't
  // mirror A→B / B→A, and the guess printed "Multi-city" on a round trip).
  const tripLabel = itinerary.length <= 1 ? 'One-way'
    : (/multi/i.test(b.trip_type || '') ? 'Multi-city'
      : /round|return/i.test(b.trip_type || '') ? 'Round trip'
        : (b.trip_type || 'Multi-city'));
  const legLabel = (i) => (itinerary.length <= 1 ? 'Flight'
    : tripLabel === 'Round trip' ? (['Onward', 'Return'][i] || `Flight ${i + 1}`)
      : `Flight ${i + 1}`);

  // Every physical sector of a leg. A non-connecting leg synthesises a single
  // sector from its own fields — the same fallback shape the customer uses.
  const segsOf = (f) => (Array.isArray(f.segments) && f.segments.length ? f.segments : [{
    fromCode: f.fromCode, toCode: f.toCode, fromCity: f.fromCity, toCity: f.toCity,
    depISO: f.depISO, arrISO: f.arrISO,
    airlineCode: iataOf(f.code), flightNo: String(f.code || '').replace(/^[A-Z0-9]{2}/i, ''),
    airlineName: f.airline, cabin: f.cabinName, pnr: f.pnr,
  }]);

  // The PNR rows the page is headed with: one per ticketed FLIGHT, in itinerary
  // order. A sector carries its own PNR and (on a codeshare/mixed itinerary) its
  // own carrier, so we key on both. Falls back to the booking-level PNR.
  const headerPnrs = (() => {
    const rows = [];
    const seen = new Set();
    for (const leg of itinerary) {
      for (const s of segsOf(leg)) {
        const segPnr = s?.pnr || leg?.pnr || (pnr !== '—' ? pnr : null);
        if (!segPnr) continue;
        const from = s?.fromCode || leg?.fromCode;
        const to = s?.toCode || leg?.toCode;
        const key = `${segPnr}|${from}-${to}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({
          key,
          pnr: segPnr,
          code: s?.airlineCode ? `${s.airlineCode}${s.flightNo || ''}` : leg?.code,
          airline: s?.airlineName || s?.airline || leg?.airline || '',
          route: from && to ? `${from}–${to}` : '',
        });
      }
    }
    // With a single flight the route caption is noise — the itinerary says it.
    return rows.length > 1 ? rows : rows.map((r) => ({ ...r, route: '' }));
  })();

  // A RESCHEDULE (reissue) grew total_paid by its charge, but every fareDetail
  // line below is the ORIGINAL ticket's — with no line of its own the summary
  // silently under-shot what was paid. Bill it from the REISSUE amendment, whose
  // amount IS the charge. SSR add-ons are deliberately not summed here: they are
  // itemised separately, so counting them again would double-bill.
  const reissueCharge = amendments
    .filter((a) => /^REISSUE$/i.test(a.type || ''))
    .reduce((s, a) => s + num(a.total_amount ?? a.amount), 0);
  const isReissued = reissueCharge > 0 || amendments.some((a) => /^REISSUE$/i.test(a.type || ''));

  // What actually came off the price at checkout (auto discount + coupon). The
  // API's `coupon_discount` is the COMBINED total, so adding checkout's
  // flightDiscount on top of it would count that half twice; the checkout blob
  // holds the two as separate, non-overlapping fields — use it, and fall back to
  // the combined field only when the blob is absent.
  const discountPaid = fd
    ? ((fd.checkout?.flightDiscount != null || fd.checkout?.couponDiscount != null)
      ? round2(num(fd.checkout?.flightDiscount) + num(fd.checkout?.couponDiscount))
      : num(b.fare?.coupon_discount))
    : 0;

  // Any gap between what the itemised lines add up to and Total Paid. Without it
  // the printed lines could fall short of (or exceed) the total with no
  // explanation — which is exactly what this document used to do.
  const otherAdjustments = fd
    ? round2(num(total) - (
      num(fd.base) + num(fd.taxTotal) + num(fd.mgmtFee)
      + num(fd.ancillary?.mealSeatBaggage) + num(fd.ancillary?.baggageProtection)
      + num(fd.ancillary?.travelInsurance) + num(fd.mgmtGst?.total)
      + num(fd.markup) + num(b.fare?.transaction_fee) + num(b.fare?.convenience_fee)
      + reissueCharge - discountPaid
    ))
    : 0;

  // ── THE printed fare lines ────────────────────────────────────────────────
  // `b.fare.summary` is composed SERVER-SIDE from this booking's own
  // `flight_bookings` columns by the module booking_backend builds the CUSTOMER's
  // copy from (admin_backend/src/utils/flightFareSummary.js, its deliberate
  // mirror) — so this console and both storefronts print the same document
  // instead of three derivations of it.
  //
  // What it replaces: `fd.markup` is `ticketsTotal − supplierTotal(printed
  // breakdown)`, two figures from different stores read at different moments, and
  // whatever failed to add up was printed as "Other adjustments". Live, on ONE
  // booking, this console showed "Other adjustments −₹2.00" while the storefront
  // showed "Discount −₹2.00" — neither was reading the ₹2.00 actually recorded in
  // `discount_amount`, because both preferred a `checkout` blob that said 0.
  // See CLAUDE.md §30.
  const svc = b.fare?.summary || null;
  const fareLines = svc
    ? {
      base: num(svc.base),
      airlineTaxes: num(svc.tax?.airlineTaxes),
      taxItems: svc.tax?.items || [],
      mgmtFee: num(svc.tax?.mgmtFee),
      mgmtGst: svc.tax?.mgmtGst || { total: 0, sgst: 0, cgst: 0 },
      addons: num(svc.addons),
      insurance: num(svc.insurance),
      processing: num(svc.processing),
      txnFee: num(svc.transaction),
      convenience: num(svc.convenience),
      reissue: num(svc.reissue),
      discount: num(svc.discount),
      other: num(svc.other),
      total: num(svc.total),
    }
    : {
      base: num(fd?.base),
      airlineTaxes: num(fd?.taxTotal),
      taxItems: (fd?.taxItems || []).map((x) => ({ code: x.code, label: x.code, amount: num(x.amount) })),
      mgmtFee: num(fd?.mgmtFee),
      mgmtGst: fd?.mgmtGst || { total: 0, sgst: 0, cgst: 0 },
      addons: num(fd?.ancillary?.mealSeatBaggage),
      insurance: num(fd?.ancillary?.travelInsurance),
      processing: num(fd?.markup),
      txnFee: num(b.fare?.transaction_fee),
      convenience: num(b.fare?.convenience_fee),
      reissue: reissueCharge,
      discount: discountPaid,
      other: otherAdjustments,
      total: num(total),
    };

  const insurance = b.insurance || null;
  const insurancePolicies = insurance?.policy?.travellers || [];
  const insuranceIssued = /success|book|issued/i.test(String(insurance?.status || ''));
  const gstInfo = b.gst_info || null;
  const bookingNotes = String(b.notes || '').trim();

  // Every operated sector of this ticket, with the PNR it was issued under and
  // the baggage the fare carries on it. A connecting ticket is flown sector by
  // sector, and the reference e-ticket lists it that way.
  const ticketSectors = itinerary.flatMap((leg) => segsOf(leg).map((sg) => ({
    route: `${sg.fromCode}-${sg.toCode}`,
    pnr: sg.pnr || leg.pnr || (pnr !== '—' ? pnr : ''),
    checkIn: sg.baggage?.checkIn || leg.baggage?.checkIn || fd?.perPax?.[0]?.checkInBaggage || '',
    cabin: sg.baggage?.cabin || leg.baggage?.cabin || fd?.perPax?.[0]?.cabinBaggage || '',
  })));

  // What this passenger holds on this sector beyond the bare ticket. Gated on
  // `confirmed_addons` — what the supplier ACTUALLY provisioned — never on the
  // raw selection, so an add-on the airline dropped is not printed as delivered
  // (payment_reconciliation owns that refund). The readable label comes from the
  // matching selection, the only place it is kept.
  const prefOf = (t, sec, paxIdx) => {
    const travellerNo = t?._no || paxIdx + 1;
    // The supplier's own per-traveller answer first — it is built per traveller,
    // so it is unambiguous and needs none of the attribution below.
    const supplied = supplierSsrLabels(t, sec.route);
    const parts = supplied && supplied.length ? [...supplied] : [];
    if (!parts.length) {
      // Fallback for a booking the live read could not enrich: reconstruct from
      // `confirmed_addons`, which flattens EVERY traveller's SSR into one list
      // with no passenger on it. Attribution is a consuming pair-up, not a test
      // — see lib/paxSectorDetail for why the old "does any selection with this
      // code carry my paxIndex" check printed two travellers' identical meal
      // twice on BOTH of their rows.
      parts.push(...confirmedAddonLabels({
        confirmed: b.confirmed_addons,
        selected: b.selected_addons,
        route: sec.route,
        paxIndex: paxIdx,
      }));
    }
    const bought = (b.addons || [])
      .filter((a) => (!a.travellerId || a.travellerId === travellerNo)
        && (!a.from || `${a.from}-${a.to}` === sec.route))
      .map((a) => a.desc || `${a.type} ${a.code}`.trim());
    parts.push(...bought);
    // NO passenger-level fallback: `t.seat` is EVERY route's seat joined into one
    // string, so on a round trip with a seat on the onward leg only, the return
    // sector would inherit the onward seat. Strictly per-sector, or nothing.
    return parts.join(', ');
  };

  // The seat(s) one passenger actually HOLDS, across every sector, joined the way
  // the customer voucher joins them (`passengers[].seat` = the live ssrSeatInfos
  // seat numbers). The BCBP barcode below has to encode the SAME value as the
  // customer's copy, and the two sources are not interchangeable: `t.seat` is
  // always null on this page (checkout sends seats in their own `seats[]`, never
  // on the traveller, and flight_passengers.seat_number is never written), so
  // reading it encoded "no seat" on every admin reprint.
  //
  // Gated on `confirmed_addons` — what the supplier PROVISIONED — for the same
  // reason prefOf is: a seat the airline dropped must not be printed, or encoded,
  // as held. The readable seat number lives on the matching selection (`label`).
  const seatsOfPax = (paxIdx) => {
    const isSeat = (x) => /seat/i.test(String(x?.type || ''));
    const selected = (b.selected_addons || []).filter(isSeat);
    const seats = [];
    for (const c of (b.confirmed_addons || []).filter(isSeat)) {
      const sameAddon = (s) => s.route === c.route && String(s.code) === String(c.code);
      const tagged = selected.filter((s) => sameAddon(s) && s.paxIndex != null);
      // Per-passenger safety, exactly as prefOf applies it: when the selections
      // carry a paxIndex, this seat belongs to that traveller alone.
      if (tagged.length && !tagged.some((s) => Number(s.paxIndex) === paxIdx)) continue;
      const label = (tagged.find((s) => Number(s.paxIndex) === paxIdx)
        || selected.find(sameAddon))?.label || c.code;
      if (label) seats.push(String(label));
    }
    return seats.join(', ');
  };

  // ONE scannable barcode per JOURNEY GROUP: a PDF417 carrying that group's IATA
  // BCBP string (passenger, PNR, sector, carrier, flight, Julian date, cabin,
  // seat) — the same 2D code airline gates read, for the group's first sector and
  // the lead passenger. Captioned with route + PNR.
  const journeyBarcodes = itinerary.map((leg, li) => {
    const segs = segsOf(leg);
    const s = segs[0] || {};
    const last = segs[segs.length - 1] || s;
    const t = pax[0] || {};
    const segPnr = s.pnr || leg.pnr || (pnr !== '—' ? pnr : ref);
    if (!segPnr) return null;
    const carrier = s.airlineCode || iataOf(leg.code);
    const flightNo = s.flightNo || String(leg.code || '').replace(/^[A-Z0-9]{2}/i, '');
    const from = s.fromCode || leg.fromCode;
    const to = last.toCode || leg.toCode;
    return {
      key: li,
      route: from && to ? `${from}–${to}` : '',
      pnr: segPnr,
      data: bcbpString({
        passenger: t, pnr: segPnr, from: s.fromCode, to: s.toCode,
        carrier, flightNo, depISO: s.depISO, cabin: s.cabin || leg.cabinName,
        // `t.seat` is the seat the SUPPLIER holds for this passenger, merged onto
        // the payload from the live booking read — the same value the customer
        // voucher encodes. It leads for that reason: the two copies of this ticket
        // must not encode different boarding data. seatsOfPax reconstructs the seat
        // from confirmed_addons and stays as the fallback for a booking the live
        // read could not reach.
        seat: t.seat || seatsOfPax(0), seq: 1,
      }),
    };
  }).filter(Boolean);

  // The barcode belonging to itinerary leg `li`, or undefined when that leg has
  // no PNR to encode. journeyBarcodes is built with `key: li`, so this is just
  // the lookup the itinerary table needs to print each leg's own barcode.
  const legBarcode = (li) => journeyBarcodes.find((b) => b.key === li);

  return (
    <>
      <div className="doc-bar">
        <button className="btn ghost" onClick={() => navigate(backToList(search, '/flight/bookings', '/flight/corporate-bookings'))}>
          <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
        </button>
        <button className="btn primary" onClick={() => window.print()}>
          <Icon name="download" /> Print / Save as PDF
        </button>
      </div>

      <article className="doc-sheet" style={{ '--tkt-accent': doc.accent }}>
        {/* Branded header — the storefront's own logo, its contact block, and the
            document kind / title / reference on the right, like the customer's. */}
        <header className="tkt-brand-head">
          <div className="tkt-brand-left">
            <DocBrandMark brandCode={b.brand} className="tkt-brand-logo" />
            <p className="muted">{COMPANY.address}</p>
            <p className="muted">{COMPANY.phone} · {COMPANY.support}</p>
          </div>
          <div className="tkt-brand-right">
            <span className="tkt-status-pill">{statusLabel}</span>
            <p className="tkt-kind">Flight E-Ticket</p>
            <h1 className="tkt-title">Itinerary &amp; Receipt</h1>
            <p className="muted mono">{ref}</p>
          </div>
        </header>

        {/* Booking time / id / status, with each ticketed flight's airline PNR
            alongside — the way the reference e-ticket heads the page. */}
        <div className="tkt-head">
          <div className="tkt-head-left">
            {b.created_at ? (
              <div><b>Booking Time:</b> <span className="muted">{fmtDateTime(b.created_at)}</span></div>
            ) : null}
            <div><b>Booking ID:</b> <span className="muted mono">{ref}</span></div>
            <div><b>Booking Status:</b> <span className="tkt-status-text">{statusLabel.toUpperCase()}</span></div>
          </div>
          {headerPnrs.length > 0 && (
            <div className="tkt-pnr-list">
              {headerPnrs.map((r) => (
                <div className="tkt-pnr-row" key={r.key}>
                  <AirlineMark code={iataOf(r.code)} name={r.airline} size={36} />
                  <span className="tkt-pnr-airline">{r.airline || ''}</span>
                  <div className="tkt-pnr-val">
                    <p className="mono">{r.pnr}</p>
                    <p className="muted">Airline PNR{r.route ? ` · ${r.route}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="tkt-meta-strip">
          <Field label="E-Ticket No." value={voucherNo} mono />
          <Field label="Booking Ref" value={ref} mono />
          <Field label="PNR" value={pnr} mono />
          <Field label="Trip Type" value={tripLabel} />
          <Field label="Booking Status" value={statusLabel} />
        </div>

        <p className="doc-h tkt-section">
          Flight Itinerary{itinerary.length > 1 ? ` · ${tripLabel} (${itinerary.length} flights)` : ''}
        </p>
        {itinerary.length === 0 ? <p className="muted">No stored itinerary.</p> : (
          <>
          {/* ONE TABLE (box) PER LEG. This was a single .doc-segs table holding
              every leg. When it split across a page its box was still painted down
              the whole remainder of page 1 — stray vertical rules under the onward
              barcode — because a block broken by a page break still paints its box
              on the page it started on. Each leg now owns its table, so nothing is
              left hanging open across the break and every section prints whole.
              The tables are separate elements, so the printer breaks BETWEEN them;
              .tkt-leg-block keeps any single one off a page boundary. Each repeats
              the column header, so a leg printed overleaf is still labelled. */}
              {itinerary.map((f, i) => {
                const segs = segsOf(f);
                return (
                  <table className="doc-segs tkt-leg-block" key={i}>
                    <thead>
                      <tr>
                        <th>Flight</th><th>Route</th><th>Date</th><th>Depart</th><th>Arrive</th><th>Terminal</th><th>Class</th><th>Baggage</th>
                      </tr>
                    </thead>
                    <tbody>
                    <tr className="leg-row">
                      <td colSpan={8}>
                        <span className="tkt-leg-line">
                          {itinerary.length > 1 && <span className="tkt-chip accent">{legLabel(i)}</span>}
                          <AirlineMark code={iataOf(f.code)} name={f.airline} size={22} />
                          <b>{f.airline} · {f.code}</b>
                          {f.pnr && <span className="tkt-chip mono">PNR {f.pnr}</span>}
                          {/* Marked against the TICKET, where the supplier prints it
                              — the booking status stays "Booked" (it is), so nothing
                              downstream that reads that string changes behaviour. */}
                          {isReissued && <span className="tkt-chip warn">Reissued</span>}
                          {segs.length === 1 && f.stops ? <span className="muted">· {f.stops} stop{f.stops > 1 ? 's' : ''}</span> : null}
                          <span className="tkt-leg-fare muted">{f.cabinName || 'Economy'} · {f.fareName || 'Published'}</span>
                        </span>
                      </td>
                    </tr>
                    {segs.map((s, si) => (
                      <Fragment key={si}>
                        <tr>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <AirlineMark code={s.airlineCode || iataOf(f.code)} name={s.airlineName} size={20} />
                              <div>
                                <b>{[s.airlineCode, s.flightNo].filter(Boolean).join(' ') || f.code}</b>
                                <div className="muted" style={{ fontSize: '.62rem' }}>{s.airlineName || s.airlineCode || f.airline || ''}</div>
                              </div>
                            </div>
                          </td>
                          <td className="route-cell">
                            <b>{s.fromCode} → {s.toCode}</b>
                            {/* NOT className="sub". That is the SIDEBAR sub-menu
                                item rule in admin.css, which draws a ::before dot,
                                sets cursor:pointer and adds a hover pill — so these
                                two caption lines rendered as clickable radio options
                                in the middle of the itinerary. */}
                            <span className="route-sub">{s.fromCity || s.fromCode} – {s.toCity || s.toCode}</span>
                            {(s.fromName || s.toName) ? (
                              <span className="route-sub">{[s.fromName, s.toName].filter(Boolean).join(' → ')}</span>
                            ) : null}
                          </td>
                          <td>{fmtDate(s.depISO)}</td>
                          <td className="time-cell">{s.dep || timeOf(s.depISO)}</td>
                          <td className="time-cell">{s.arr || timeOf(s.arrISO)}</td>
                          {/* Raw values, joined — exactly as the customer voucher does.
                              Prefixing "Terminal " printed "Terminal Terminal 1",
                              because the stored value already contains the word. */}
                          <td>{[s.fromTerminal, s.toTerminal].filter(Boolean).join(' → ') || '—'}</td>
                          <td>{s.cabin || f.cabinName || 'Economy'}</td>
                          <td>
                            {/* The airline's own allowance strings — never
                                substituted with a number of our own; "NA" is the
                                honest answer when the fare carries no allowance. */}
                            <div>{f.baggage?.cabin || 'NA'}</div>
                            <div>{f.baggage?.checkIn || 'NA'}</div>
                          </td>
                        </tr>
                        {si < segs.length - 1 && layoverMin(segs[si].arrISO, segs[si + 1].depISO) > 0 ? (
                          <tr className="layover-row">
                            <td colSpan={8}>
                              Layover {fmtMin(layoverMin(segs[si].arrISO, segs[si + 1].depISO))} · {segs[si].toCity || segs[si].toCode} ({segs[si].toCode})
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                    {/* Boarding barcode for THIS leg, as the leg's closing row —
                        moved here from the single panel that used to sit above
                        the itinerary. Barcodes printed side by side gave no clue
                        which one belonged to which flight; under the leg it
                        scans for, an onward/return/multi-city ticket reads one
                        journey at a time. Same journeyBarcodes entry (keyed by
                        leg index) as before — only WHERE it renders moved, and
                        it reuses the existing .tkt-bc / .tkt-verify-* rules, so
                        no stylesheet change was needed. */}
                    {legBarcode(i) ? (
                      <tr>
                        <td colSpan={8}>
                          <div className="tkt-bc">
                            <span className="tkt-verify-label">Boarding Verification</span>
                            <span className="tkt-verify-hint">Present this barcode at check-in / security</span>
                            <BoardingBarcode value={legBarcode(i).data} />
                            <span className="mono">{legBarcode(i).route ? `${legBarcode(i).route} · ` : ''}{legBarcode(i).pnr}</span>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    </tbody>
                  </table>
                );
              })}
          </>
        )}

        <p className="doc-h tkt-section">Passenger Details</p>
        <table className="doc-table tkt-pax">
          <colgroup>
            {[5, 22, 12, 15, 15, 17, 14].map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}
          </colgroup>
          <thead>
            <tr>
              <th>Sr</th>
              <th>Passenger</th>
              <th>Sector</th>
              <th>PNR<br /><small className="muted">Ticket no.</small></th>
              <th>Baggage<br /><small className="muted">Check-in | Cabin</small></th>
              <th>Meal &amp; Seat<br /><small className="muted">Preferences</small></th>
              <th>Document<br /><small className="muted">ID</small></th>
            </tr>
          </thead>
          <tbody>
            {pax.length === 0 ? (
              <tr><td colSpan={7}>No passenger records.</td></tr>
            ) : pax.flatMap((t, i) => {
              // ONE TABLE ROW PER SECTOR (like the supplier's e-ticket): Sr,
              // Passenger and Document span the sector rows (rowSpan) while
              // Sector, PNR, Baggage and Meal each get their own aligned row.
              const secs = ticketSectors.length ? ticketSectors : [{ route: '—' }];
              return secs.map((sec, si) => (
                <tr key={`${i}-${si}`}>
                  {si === 0 && <td rowSpan={secs.length} className="muted">{i + 1}</td>}
                  {si === 0 && (
                    <td rowSpan={secs.length}>
                      <b className="tkt-pax-name">{[t.title, t.firstName, t.lastName].filter(Boolean).join(' ')}</b>
                      <span className="muted tkt-pax-sub">{paxTypeName(t.type)}</span>
                      {t.dob ? <span className="muted tkt-pax-sub">{voucherDmy(t.dob)}</span> : null}
                      {t.passportNumber ? (
                        <span className="muted tkt-pax-sub">PP: {t.passportNumber}{t.nationality ? ` · N: ${t.nationality}` : ''}</span>
                      ) : null}
                      {(t.passportIssue || t.passportExpiry) ? (
                        <span className="muted tkt-pax-sub">ID: {[t.passportIssue, t.passportExpiry].filter(Boolean).map(voucherDmy).join(', ')}</span>
                      ) : null}
                      {ffLabels(t).map((l, k) => <span key={k} className="muted tkt-pax-sub">FF: {l}</span>)}
                    </td>
                  )}
                  <td>{sec.route}</td>
                  <td>
                    <b className="mono">{sec.pnr || 'NA'}</b>
                    <span className="muted tkt-pax-sub mono">{ticketNoOf(t, sec, si) || 'NA'}</span>
                  </td>
                  <td>{sec.checkIn || 'NA'} | {sec.cabin || 'NA'}</td>
                  <td>{prefOf(t, sec, i) || 'NA'}</td>
                  {si === 0 && <td rowSpan={secs.length}>{t.documentId || 'NA'}</td>}
                </tr>
              ));
            })}
          </tbody>
        </table>

        {/* GST Details — only when the traveller supplied a GSTIN at checkout.
            Same five columns, in the same order, as the supplier's own e-ticket. */}
        {gstInfo?.gstNumber && (
          <>
            <p className="doc-h tkt-section">GST Details</p>
            <table className="doc-table doc-gst">
              <thead>
                <tr><th>GST Name</th><th>GST No</th><th>GST Address</th><th>GST email</th><th>GST Phone</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td><b>{gstInfo.registeredName || '—'}</b></td>
                  <td className="mono">{gstInfo.gstNumber}</td>
                  <td>{gstInfo.address || '—'}</td>
                  <td>{gstInfo.email || '—'}</td>
                  <td>{gstInfo.mobile || '—'}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        <div className="doc-summary">
          <div>
            <p className="doc-h tkt-section">Contact</p>
            <Field label="Email" value={b.contact?.email || b.customer_email} />
            <Field label="Mobile" value={b.contact?.phone} />
          </div>
          <div>
            <p className="doc-h tkt-section">Fare Breakdown</p>
            <div className="tkt-fare">
              {fd ? (
                <>
                  {/* Base Fare INCLUDES our processing fee (no line of its own). */}
                  <FareRow label={`Base Fare (${pax.length} pax)`} amount={fareLines.base + fareLines.processing} />
                  <FareRow
                    label="Airline Taxes &amp; Fees"
                    amount={fareLines.airlineTaxes}
                    sub={(fareLines.taxItems || []).map((x) => `${x.code} ${inr2(x.amount)}`).join('  ·  ')}
                  />
                  {fareLines.mgmtFee > 0 && <FareRow label="Management Fee" amount={fareLines.mgmtFee} />}
                  {fareLines.addons > 0 && (
                    <FareRow label="Meal / Seat / Baggage / Misc Charges" amount={fareLines.addons} />
                  )}
                  {fareLines.insurance > 0 && (
                    <FareRow
                      label="Travel Insurance"
                      amount={fareLines.insurance}
                      sub={insurance?.insurer ? `${insurance.insurer}${insurance.tier ? ` · ${insurance.tier}` : ''}` : ''}
                    />
                  )}
                  {fareLines.mgmtGst?.total > 0 && (
                    <FareRow
                      label="Management Fee GST"
                      amount={fareLines.mgmtGst.total}
                      sub={`SGST (9%) ${inr2(fareLines.mgmtGst.sgst)}   ·   CGST (9%) ${inr2(fareLines.mgmtGst.cgst)}`}
                    />
                  )}
                  {/* Our margin and the gateway's cut — the amounts RECORDED on the
                      booking, not a subtraction between two supplier figures (§30). */}
                  <FareRow label={b.fare?.convenience_label || 'Convenience Fee'} amount={fareLines.convenience} />
                  {fareLines.txnFee > 0 && <FareRow label="Transaction Fee" amount={fareLines.txnFee} />}
                  {fareLines.reissue > 0 && <FareRow label="Reschedule charge" amount={fareLines.reissue} />}
                  {fareLines.discount > 0 && <FareRow label={b.fare?.discount_label || 'Discount'} amount={-fareLines.discount} />}
                  {/* Zero on every booking the ledger can describe. Kept HERE, on the
                      console, so a row it CANNOT describe is visible to staff. */}
                  {Math.abs(fareLines.other) >= 0.01 && <FareRow label="Other adjustments" amount={fareLines.other} />}
                </>
              ) : (
                /* Pre-capture bookings have no itemised breakdown to show — an
                   honest two-line summary rather than invented components. */
                <>
                  <FareRow label={`Base Fare (${pax.length} pax)`} amount={b.fare?.base_fare} />
                  <FareRow label="Taxes &amp; Fees" amount={Math.max(0, total - num(b.fare?.base_fare))} />
                </>
              )}
              <div className="tkt-fare-total">
                <span>Total Paid</span><span>{inr2(total)}</span>
              </div>
            </div>
            <p className="muted tkt-fare-note">All amounts in INR, inclusive of applicable taxes.</p>
          </div>
        </div>

        {bookingNotes && (
          <>
            <p className="doc-h tkt-section">Booking Notes</p>
            <div className="tkt-note">{bookingNotes}</div>
          </>
        )}

        {insurance && (
          <>
            <p className="doc-h tkt-section">Travel Insurance</p>
            {!insuranceIssued ? (
              <div className="tkt-warn">
                A travel-insurance premium of <b>{inr2(num(insurance.premium))}</b> was charged, but the insurer has
                <b> not issued a policy</b> ({insurance.status || 'unknown status'}). The passenger is <b>not covered</b>.
                Reconcile and refund the premium against this booking reference.
              </div>
            ) : (
              <>
                <div className="tkt-ins-head">
                  <Field label="Insurer" value={insurance.insurer} />
                  <Field label="Plan" value={insurance.tier} />
                  <Field label="Cover" value={insurance.cover} />
                  <Field label="Premium" value={inr2(num(insurance.premium))} />
                </div>
                <table className="doc-table doc-gst">
                  <thead>
                    <tr><th>Insured Passenger</th><th>Policy No.</th><th>Cover From</th><th>Cover To</th></tr>
                  </thead>
                  <tbody>
                    {(insurancePolicies.length ? insurancePolicies : [{ name: '—', policyId: 'NA' }]).map((t, i) => (
                      <tr key={i}>
                        <td><b>{t.name || '—'}</b></td>
                        <td className="mono">{t.policyId || 'NA'}</td>
                        <td>{fmtDate(insurance.coverFrom)}</td>
                        <td>{fmtDate(insurance.coverTo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}

        <p className="doc-h tkt-section">Important Travel Instructions</p>
        <div className="doc-terms">
          <ul>
            <li>Complete web check-in via the airline as per its policy. Reach the airport at least <b>2 hours</b> before domestic and <b>3–4 hours</b> before international departures.</li>
            <li>Carry a valid government photo ID (and a passport with valid visa for international travel) matching the passenger name on this ticket <b>exactly</b>.</li>
            <li>The free baggage allowance shown per flight above is set by the airline &amp; fare; excess baggage is charged by the airline at the airport.</li>
            <li>Flight timings are controlled by the airline and may change — reconfirm your flight status directly with the airline before departure.</li>
            <li>Infants must be accompanied by an adult; date-of-birth proof may be required at check-in for infant/child fares.</li>
            <li>Name changes are not permitted after ticketing; date/route changes and cancellations are subject to airline fare rules and applicable charges.</li>
          </ul>
        </div>

        <p className="doc-h tkt-section">Terms &amp; Conditions / Disclaimer</p>
        <p className="tkt-disclaimer">
          This e-ticket is issued by {COMPANY.name} as a booking agent; the contract of carriage is between the
          passenger and the operating airline and is governed by the airline’s conditions of carriage and fare rules.
          {' '}{COMPANY.name} is not liable for schedule changes, delays, cancellations, denied boarding, or loss/damage
          of baggage by the airline. Fares, taxes and fees are confirmed at the time of booking and may be revised by the
          airline or authorities. Refunds and date changes, where permitted, follow the airline’s fare rules and
          applicable convenience/service charges. Please verify all passenger names, dates and timings immediately and
          report any discrepancy before travel. This document is valid only when the booking status above reads
          {' '}<b>Confirmed</b> with a valid PNR.
        </p>
      </article>
    </>
  );
}
